package org.example.project.screens_sys

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import org.example.project.enginekartei.EngineKarteiField
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.utils.All
import org.example.project.utils.All.AllNames.names
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.value
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text

private fun unternehmenField(fieldId: String): EngineKarteiField =
    EngineKarteiField(fieldId, names[fieldId] ?: fieldId)

private fun unternehmenRoleLabel(value: String): String = when (value.trim().uppercase()) {
    "PRODUZENT" -> "Produzent"
    "LOGISTIKER" -> "Logistiker"
    "HÄNDLER" -> "Händler"
    else -> value
}

private fun unternehmenDisplayState(state: All): All =
    state.copy(position = unternehmenRoleLabel(state.position))

private const val unternehmenPage1Label = "Unternehmen"
private const val unternehmenPage2Label = "Adresse"
private const val unternehmenPage3Label = "Kontakt"

val unternehmenstammKarteiPages = listOf(
    All(id = "page_1", bezeichnung = unternehmenPage1Label),
    All(id = "page_2", bezeichnung = unternehmenPage2Label),
    All(id = "page_3", bezeichnung = unternehmenPage3Label)
)

private val unternehmenFields = listOf(
    "Stammdaten" to listOf(
        "firma" to "Firma",
        "geschaeftsfuehrer" to "Gründer",
        "webseite" to "Webseite",
        "gegruendetAm" to "Gegründet am"
    ),
    "Adresse" to listOf(
        "strasse" to "Straße",
        "hausnummer" to "Hausnummer",
        "plz" to "PLZ",
        "ort" to "Ort"
    ),
    "Kontakt" to listOf(
        "email" to "E-Mail",
        "telefon" to "Telefon",
        "mobil" to "Mobil"
    )
)

private val abrechnungFields = listOf(
    "Preise" to listOf(
        "onboarding" to "Onboarding",
        "lizenzMonatlich" to "Lizenz monatlich",
        "crSatz" to "CR Satz",
        "extraMeeting" to "Extra Meeting",
        "extraSchulungRemote" to "Extra Schulung Remote"
    ),
    "Abrechnung" to listOf(
        "steuernummer" to "Steuernummer",
        "finanzamt" to "Finanzamt",
        "umsatzsteuerId" to "Umsatzsteuer ID",
        "kreditinstitut" to "Kreditinstitut",
        "iban" to "IBAN"
    )
)

private const val UNTERNEHMENSTAMM_FIRMEN_ID = "ls1000"

@Composable
fun Unternehmenstamm() {
    val scope = rememberCoroutineScope()
    val firmenId = UNTERNEHMENSTAMM_FIRMEN_ID
    var unternehmen by remember { mutableStateOf(All(id = "unternehmenstamm")) }
    var abrechnung by remember { mutableStateOf(All(id = "__abrechnung__")) }
    var saving by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf("") }

    suspend fun reload() {
        val loadedUnternehmen = UnternehmenstammApi.loadUnternehmen(firmenId)
        unternehmen = unternehmenDisplayState(
            loadedUnternehmen ?: All(id = "unternehmenstamm", firmenId = firmenId)
        )
        abrechnung = AbrechnungsverwaltungApi.loadAbrechnung(firmenId)
            ?: All(id = "__abrechnung__", firmenId = firmenId)
    }

    fun update(field: String, value: String) {
        unternehmen = when (field) {
            "firma" -> unternehmen.copy(firma = value)
            "geschaeftsfuehrer" -> unternehmen.copy(geschaeftsfuehrer = value)
            "webseite" -> unternehmen.copy(webseite = value)
            "gegruendetAm" -> unternehmen.copy(gegruendetAm = value)
            "strasse" -> unternehmen.copy(strasse = value)
            "hausnummer" -> unternehmen.copy(hausnummer = value)
            "plz" -> unternehmen.copy(plz = value)
            "ort" -> unternehmen.copy(ort = value)
            "email" -> unternehmen.copy(email = value)
            "telefon" -> unternehmen.copy(telefon = value)
            "mobil" -> unternehmen.copy(mobil = value)
            else -> unternehmen
        }
        message = ""
    }

    fun updateAbrechnung(field: String, value: String) {
        abrechnung = when (field) {
            "onboarding" -> abrechnung.copy(onboarding = value)
            "lizenzMonatlich" -> abrechnung.copy(lizenzMonatlich = value)
            "crSatz" -> abrechnung.copy(crSatz = value)
            "extraMeeting" -> abrechnung.copy(extraMeeting = value)
            "extraSchulungRemote" -> abrechnung.copy(extraSchulungRemote = value)
            "supportPreis" -> abrechnung.copy(supportPreis = value)
            "cloudKosten" -> abrechnung.copy(cloudKosten = value)
            "archivierung" -> abrechnung.copy(archivierung = value)
            "backupkosten" -> abrechnung.copy(backupkosten = value)
            "setupkosten" -> abrechnung.copy(setupkosten = value)
            "steuernummer" -> abrechnung.copy(steuernummer = value)
            "finanzamt" -> abrechnung.copy(finanzamt = value)
            "umsatzsteuerId" -> abrechnung.copy(umsatzsteuerId = value)
            "kreditinstitut" -> abrechnung.copy(kreditinstitut = value)
            "iban" -> abrechnung.copy(iban = value)
            else -> abrechnung
        }
        message = ""
    }

    fun save() {
        if (saving) return
        saving = true
        message = ""
        scope.launch {
            val savedUnternehmen = runCatching {
                UnternehmenstammApi.saveUnternehmen(unternehmen.copy(id = "unternehmenstamm", firmenId = firmenId), firmenId)
            }.getOrNull()
            val savedAbrechnung = runCatching {
                AbrechnungsverwaltungApi.saveAbrechnung(abrechnung.copy(id = "__abrechnung__", firmenId = firmenId), firmenId)
            }.getOrNull()
            if (savedUnternehmen == null || savedAbrechnung == null) {
                message = "Unternehmen konnte nicht vollständig gespeichert werden."
            } else {
                reload()
                message = "Unternehmen gespeichert."
            }
            saving = false
        }
    }

    LaunchedEffect(firmenId) { reload() }

    Style(SysCardStyles)
    Div({ classes(SysCardStyles.page) }) {
        Div({ classes(SysCardStyles.shell) }) {
            SysHeader(
                icon = "images/domain.svg",
                title = unternehmen.firma.ifBlank { "Unternehmenstamm" },
                saving = saving,
                onSave = ::save
            )
            if (message.isNotBlank()) {
                Span({ classes(SysCardStyles.message) }) { Text(message) }
            }
            Div({ classes(SysCardStyles.content) }) {
                unternehmenFields.forEach { (title, fields) ->
                    SysFormSection(
                        title = title,
                        fields = fields,
                        fieldValue = { field -> unternehmenValue(unternehmen, field) },
                        readonly = { field -> field == "position" || field == "snapKey" },
                        onChange = ::update
                    )
                }
                abrechnungFields.forEach { (title, fields) ->
                    SysFormSection(
                        title = title,
                        fields = fields,
                        fieldValue = { field -> abrechnungValue(abrechnung, field) },
                        onChange = ::updateAbrechnung
                    )
                }
            }
        }
    }
}

private fun unternehmenValue(state: All, field: String): String =
    when (field) {
        "firma" -> state.firma
        "geschaeftsfuehrer" -> state.geschaeftsfuehrer
        "position" -> state.position
        "snapKey" -> state.snapKey
        "webseite" -> state.webseite
        "gegruendetAm" -> state.gegruendetAm
        "strasse" -> state.strasse
        "hausnummer" -> state.hausnummer
        "plz" -> state.plz
        "ort" -> state.ort
        "email" -> state.email
        "telefon" -> state.telefon
        "mobil" -> state.mobil
        else -> ""
    }

private fun abrechnungValue(state: All, field: String): String =
    when (field) {
        "onboarding" -> state.onboarding
        "lizenzMonatlich" -> state.lizenzMonatlich
        "crSatz" -> state.crSatz
        "extraMeeting" -> state.extraMeeting
        "extraSchulungRemote" -> state.extraSchulungRemote
        "supportPreis" -> state.supportPreis
        "cloudKosten" -> state.cloudKosten
        "archivierung" -> state.archivierung
        "backupkosten" -> state.backupkosten
        "setupkosten" -> state.setupkosten
        "steuernummer" -> state.steuernummer
        "finanzamt" -> state.finanzamt
        "umsatzsteuerId" -> state.umsatzsteuerId
        "kreditinstitut" -> state.kreditinstitut
        "iban" -> state.iban
        else -> ""
    }

@Composable
private fun SysHeader(icon: String, title: String, saving: Boolean, onSave: () -> Unit) {
    Div({ classes(SysCardStyles.topbar) }) {
        Div({ classes(SysCardStyles.header) }) {
            Div({ classes(SysCardStyles.iconBox) }) {
                Img(src = icon, attrs = { classes(SysCardStyles.icon) })
            }
            H3({ classes(SysCardStyles.title) }) { Text(title) }
        }
        LumaticaButton(
            label = if (saving) "Speichern..." else "Speichern",
            onClick = onSave,
            variant = LumaticaButtonVariant.Action,
            enabled = !saving,
            heightPx = 36
        )
    }
}

@Composable
private fun SysFormSection(
    title: String,
    fields: List<Pair<String, String>>,
    fieldValue: (String) -> String,
    readonly: (String) -> Boolean = { false },
    onChange: (String, String) -> Unit
) {
    Div({ classes(SysCardStyles.sectionBlock) }) {
        Div({ classes(SysCardStyles.sectionTitle) }) { Text(title) }
        Div({ classes(SysCardStyles.fieldGrid) }) {
            fields.forEach { (field, label) ->
                Div({ classes(SysCardStyles.fieldRow) }) {
                    Span({ classes(SysCardStyles.fieldLabel) }) { Text(label) }
                    Input(InputType.Text, attrs = {
                        classes(SysCardStyles.input)
                        value(fieldValue(field))
                        placeholder("")
                        if (readonly(field)) {
                            attr("readonly", "readonly")
                        } else {
                            onInput { onChange(field, it.value) }
                        }
                    })
                }
            }
        }
    }
}

private object SysCardStyles : StyleSheet() {
    val page by style {
        width(100.percent)
        height(100.percent)
        minHeight(0.px)
        boxSizing("border-box")
        padding(18.px, 22.px)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Stretch)
        color(Color.white)
        overflow("hidden")
    }
    val shell by style {
        width(100.percent)
        maxWidth(1320.px)
        minHeight(0.px)
        property("margin", "0 auto")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(18.px)
    }
    val topbar by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween)
        gap(12.px)
        padding(18.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.12))
        borderRadius(6.px)
        backgroundColor(rgba(255, 255, 255, 0.035))
    }
    val header by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(10.px)
        minWidth(0.px)
    }
    val iconBox by style {
        width(72.px)
        height(72.px)
        borderRadius(6.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        backgroundColor(rgba(255, 255, 255, 0.07))
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.12))
        boxSizing("border-box")
    }
    val icon by style {
        width(38.px)
        height(38.px)
        property("filter", "brightness(0) invert(1)")
        opacity(0.88)
    }
    val title by style {
        margin(0.px)
        fontSize(20.px)
        fontWeight("900")
    }
    val message by style {
        color(Color("#84e1a7"))
        fontSize(12.px)
        fontWeight("700")
    }
    val content by style {
        minHeight(0.px)
        overflowY("auto")
        paddingRight(4.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(18.px)
    }
    val sectionBlock by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(18.px)
        padding(22.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.13))
        borderRadius(6.px)
        backgroundColor(rgba(255, 255, 255, 0.035))
        boxSizing("border-box")
    }
    val sectionTitle by style {
        marginTop(0.px)
        marginBottom(8.px)
        color(rgba(255, 255, 255, 0.88))
        fontSize(17.px)
        fontWeight("900")
        textAlign("center")
    }
    val fieldGrid by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "repeat(3, minmax(0, 1fr))")
        gap(14.px)
        media(mediaMaxWidth(980.px)) {
            self style { property("grid-template-columns", "repeat(2, minmax(0, 1fr))") }
        }
        media(mediaMaxWidth(640.px)) {
            self style { property("grid-template-columns", "1fr") }
        }
    }
    val fieldRow by style {
        minWidth(0.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(5.px)
    }
    val fieldLabel by style {
        color(rgba(255, 255, 255, 0.62))
        fontSize(10.px)
        fontWeight("800")
        property("text-transform", "uppercase")
        letterSpacing(0.px)
    }
    val input by style {
        height(34.px)
        width(100.percent)
        boxSizing("border-box")
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.16))
        borderRadius(5.px)
        backgroundColor(rgba(255, 255, 255, 0.06))
        color(Color.white)
        padding(0.px, 10.px)
        fontSize(12.px)
        property("outline", "none")
    }
}
